import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import api from '@/lib/api';
import { MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

import { ChatHeader } from '@/components/support/ChatHeader';
import { ChatMessages } from '@/components/support/ChatMessages';
import { ChatInput } from '@/components/support/ChatInput';
import logger from '@/lib/logger';

export default function SupportChatBubble() {
  const { user } = useAuth();
  const ws = useWebSocket();
  const [open, setOpen] = useState(false);
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [unread, setUnread] = useState(0);
  const [npsScore, setNpsScore] = useState(null);
  const [npsComment, setNpsComment] = useState('');
  const [npsSubmitted, setNpsSubmitted] = useState(false);
  const [npsSubmitting, setNpsSubmitting] = useState(false);
  const messagesEndRef = useRef(null);
  const fileRef = useRef(null);

  const isAdmin = ['admin','super_admin'].includes(user?.role);

  const scrollToBottom = () => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const fetchMessages = useCallback(async (chatId) => {
    if (!chatId) return;
    try {
      const r = await api.getSupportMessages(chatId);
      setMessages(r.data);
      setUnread(0);
      scrollToBottom();
    } catch (err) { logger.error(err); }
  }, []);

  const initChat = useCallback(async () => {
    try {
      const r = await api.createSupportChat();
      setChat(r.data);
      await fetchMessages(r.data.id);
    } catch (err) { logger.error(err); }
  }, [fetchMessages]);

  useEffect(() => {
    if (isAdmin) return;
    if (open && !chat) initChat();
    else if (open && chat) fetchMessages(chat.id);
  }, [open, chat, initChat, fetchMessages, isAdmin]);

  useEffect(() => {
    if (isAdmin || !ws?.addListener) return;
    const unsub1 = ws.addListener('support_message', (data) => {
      if (chat && data.chat_id === chat.id) {
        fetchMessages(chat.id);
        if (!open) setUnread(prev => prev + 1);
      }
    });
    const unsub2 = ws.addListener('support_resolved', (data) => {
      if (chat && data.chat_id === chat.id) {
        fetchMessages(chat.id);
        setNpsSubmitted(false);
        setNpsScore(null);
        setNpsComment('');
      }
    });
    return () => { unsub1(); unsub2(); };
  }, [ws, chat, open, fetchMessages, isAdmin]);

  if (isAdmin) return null;

  const handleSend = async () => {
    if (!input.trim() || !chat) return;
    setSending(true);
    try {
      await api.sendSupportMessage(chat.id, input);
      setInput('');
      await fetchMessages(chat.id);
    } catch (err) { logger.error(err); }
    setSending(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !chat) return;
    setUploading(true);
    try {
      const r = await api.uploadSupportAttachment(chat.id, file);
      await api.sendSupportMessage(chat.id, `[Attached: ${file.name}]`, r.data.path);
      await fetchMessages(chat.id);
    } catch (err) { logger.error(err); }
    setUploading(false);
    e.target.value = '';
  };

  const handleNpsSubmit = async () => {
    if (npsScore === null || !chat) return;
    setNpsSubmitting(true);
    try {
      await api.submitFeedback(chat.id, npsScore, npsComment);
      setNpsSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to submit';
      if (msg.includes('already submitted')) setNpsSubmitted(true);
      else toast.error(msg);
    }
    setNpsSubmitting(false);
  };

  const npsProps = { npsScore, setNpsScore, npsComment, setNpsComment, npsSubmitted, npsSubmitting, onSubmit: handleNpsSubmit };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 lg:bottom-6 right-4 lg:right-6 w-12 h-12 lg:w-14 lg:h-14 bg-[#0047AB] text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center z-40"
          data-testid="support-chat-bubble"
        >
          <MessageCircle className="w-5 h-5 lg:w-6 lg:h-6" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unread}</span>
          )}
        </button>
      )}

      {open && (
        <div className="fixed bottom-0 right-0 w-full h-[85vh] sm:bottom-6 sm:right-6 sm:w-[360px] sm:h-[500px] bg-white border border-slate-200 sm:rounded-lg shadow-2xl flex flex-col z-50 overflow-hidden" data-testid="support-chat-window">
          <ChatHeader chat={chat} onClose={() => setOpen(false)} />
          <ChatMessages messages={messages} messagesEndRef={messagesEndRef} npsProps={npsProps} />
          <ChatInput input={input} setInput={setInput} sending={sending} uploading={uploading} fileRef={fileRef} onSend={handleSend} onFileUpload={handleFileUpload} />
        </div>
      )}
    </>
  );
}
